/**
 * Offline queue – when back online, replay queued mutations.
 * On conflict (server updated after our cache version), surface to ConflictContext for admin resolution.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as matchApi from '../api/matchApi';
import { enqueue, getQueueItems, removeFromQueue, isOnline } from '../services/offlineQueue';
import { patchScheduleMatch, patchDashboardMatch } from '../api/readApi';
import { useConflict } from './ConflictContext';

const OfflineQueueContext = createContext();

export function useOfflineQueue() {
  return useContext(OfflineQueueContext);
}

export function OfflineQueueProvider({ children }) {
  const [pendingCount, setPendingCount] = useState(0);
  const { addConflict } = useConflict();

  const refreshPendingCount = useCallback(() => {
    setPendingCount(getQueueItems().length);
  }, []);

  const replayQueue = useCallback(async (gameId = 'table-tennis') => {
    const items = getQueueItems();
    for (const item of items) {
      if (item.type === 'matchRescheduleComplete' && item.payload) {
        try {
          const { matchId, scheduledAt, scheduledDate, scheduledTime, venueBlockId, actorName } = item.payload;
          const clientUpdatedAt = item.cacheVersion?.updatedAt ?? null;
          const result = await matchApi.matchRescheduleComplete(matchId, {
            scheduledAt,
            scheduledDate,
            scheduledTime,
            venueBlockId,
            clientUpdatedAt,
          }, actorName);
          if (result?.conflict && result?.serverMatch) {
            addConflict({
              matchId,
              serverMatch: result.serverMatch,
              pendingPayload: item.payload,
              queueItemId: item.id,
            });
            continue;
          }
          removeFromQueue(item.id);
          const matchUpdates = {
            scheduledAt: item.payload.scheduledAt,
            scheduledDate: item.payload.scheduledDate,
            scheduledTime: item.payload.scheduledTime,
            venueBlockId: item.payload.venueBlockId,
            rescheduleRequested: false,
            rescheduleApproved: false,
            updatedAt: Date.now(),
          };
          patchScheduleMatch(gameId, matchId, matchUpdates);
          patchDashboardMatch(gameId, matchId, matchUpdates);
          window.dispatchEvent(new CustomEvent('scheduleCachePatched', { detail: { gameId } }));
        } catch (err) {
          console.warn('[OfflineQueue] Replay failed for', item.id, err?.message);
        }
      }
    }
    refreshPendingCount();
  }, [addConflict, refreshPendingCount]);

  useEffect(() => {
    const handleOnline = () => {
      refreshPendingCount();
      if (getQueueItems().length > 0) replayQueue();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [replayQueue, refreshPendingCount]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  const queueReschedule = useCallback((payload, cacheVersion) => {
    enqueue({ type: 'matchRescheduleComplete', payload, cacheVersion });
    refreshPendingCount();
  }, [refreshPendingCount]);

  const value = {
    isOnline: () => isOnline(),
    pendingCount,
    queueReschedule,
    replayQueue,
    refreshPendingCount,
  };

  return (
    <OfflineQueueContext.Provider value={value}>
      {children}
    </OfflineQueueContext.Provider>
  );
}
